import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label } from '@/components/ui'
import { Clock, Copy, Check, RefreshCw, ArrowRightLeft } from 'lucide-react'
import { toast } from 'sonner'

export function Timestamp() {
  const [currentTimestamp, setCurrentTimestamp] = useState(Math.floor(Date.now() / 1000))
  const [inputTimestamp, setInputTimestamp] = useState('')
  const [inputDate, setInputDate] = useState('')
  const [inputTime, setInputTime] = useState('')
  const [convertedDate, setConvertedDate] = useState<Date | null>(null)
  const [convertedTimestamp, setConvertedTimestamp] = useState<number | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimestamp(Math.floor(Date.now() / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(label)
      toast.success('Copied to clipboard!')
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const convertTimestampToDate = () => {
    const ts = parseInt(inputTimestamp)
    if (isNaN(ts)) {
      toast.error('Invalid timestamp')
      return
    }

    // Handle milliseconds vs seconds
    const timestamp = ts > 9999999999 ? ts : ts * 1000
    const date = new Date(timestamp)

    if (isNaN(date.getTime())) {
      toast.error('Invalid timestamp')
      return
    }

    setConvertedDate(date)
    toast.success('Converted!')
  }

  const convertDateToTimestamp = () => {
    if (!inputDate) {
      toast.error('Please enter a date')
      return
    }

    const dateTimeString = `${inputDate}T${inputTime || '00:00:00'}`
    const date = new Date(dateTimeString)

    if (isNaN(date.getTime())) {
      toast.error('Invalid date/time')
      return
    }

    setConvertedTimestamp(Math.floor(date.getTime() / 1000))
    toast.success('Converted!')
  }

  const setNow = () => {
    const now = new Date()
    setInputDate(now.toISOString().split('T')[0])
    setInputTime(now.toTimeString().slice(0, 8))
  }

  const formatDate = (date: Date, format: string): string => {
    const options: Record<string, Intl.DateTimeFormatOptions> = {
      full: { dateStyle: 'full', timeStyle: 'long' },
      long: { dateStyle: 'long', timeStyle: 'medium' },
      short: { dateStyle: 'short', timeStyle: 'short' },
      iso: {},
    }

    if (format === 'iso') {
      return date.toISOString()
    }

    return new Intl.DateTimeFormat('en-US', options[format]).format(date)
  }

  const commonTimestamps = [
    { label: '1 hour ago', value: currentTimestamp - 3600 },
    { label: '24 hours ago', value: currentTimestamp - 86400 },
    { label: '7 days ago', value: currentTimestamp - 604800 },
    { label: '30 days ago', value: currentTimestamp - 2592000 },
    { label: '1 year ago', value: currentTimestamp - 31536000 },
    { label: 'Unix Epoch', value: 0 },
    { label: 'Y2K', value: 946684800 },
    { label: 'Year 2038 Problem', value: 2147483647 },
  ]

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Clock className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Timestamp Converter</h1>
            <p className="text-muted-foreground">Convert between Unix timestamps and dates</p>
          </div>
        </div>
      </div>

      {/* Current Time */}
      <Card className="glass">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Current Unix Timestamp</p>
              <p className="text-4xl font-mono font-bold text-primary">{currentTimestamp}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">Current Date/Time</p>
              <p className="text-lg font-mono">{new Date().toLocaleString()}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => copyToClipboard(currentTimestamp.toString(), 'current')}
            >
              {copied === 'current' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Timestamp to Date */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Timestamp → Date
            </CardTitle>
            <CardDescription>Convert Unix timestamp to human-readable date</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Unix Timestamp</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={inputTimestamp}
                  onChange={(e) => setInputTimestamp(e.target.value)}
                  placeholder="e.g., 1704067200"
                  className="font-mono"
                />
                <Button onClick={convertTimestampToDate}>
                  <ArrowRightLeft className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Supports both seconds and milliseconds</p>
            </div>

            {convertedDate && (
              <div className="space-y-3 p-4 bg-muted/20 rounded-lg">
                <p className="text-sm font-medium">Converted Date:</p>
                {['full', 'long', 'short', 'iso'].map((format) => (
                  <div key={format} className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground capitalize">{format}</p>
                      <p className="font-mono text-sm truncate">{formatDate(convertedDate, format)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => copyToClipboard(formatDate(convertedDate, format), format)}
                    >
                      {copied === format ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Date to Timestamp */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Date → Timestamp
            </CardTitle>
            <CardDescription>Convert date/time to Unix timestamp</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={inputDate}
                  onChange={(e) => setInputDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  step="1"
                  value={inputTime}
                  onChange={(e) => setInputTime(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={setNow} className="flex-1">
                <RefreshCw className="h-4 w-4 mr-2" />
                Now
              </Button>
              <Button onClick={convertDateToTimestamp} className="flex-1">
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                Convert
              </Button>
            </div>

            {convertedTimestamp !== null && (
              <div className="p-4 bg-muted/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Unix Timestamp:</p>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-2xl font-mono font-bold text-primary">{convertedTimestamp}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Milliseconds: {convertedTimestamp * 1000}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(convertedTimestamp.toString(), 'ts-sec')}
                    >
                      {copied === 'ts-sec' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Common Timestamps */}
      <Card className="glass">
        <CardHeader>
          <CardTitle>Common Timestamps</CardTitle>
          <CardDescription>Quick reference for common timestamps</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {commonTimestamps.map((item) => (
              <div
                key={item.label}
                className="p-3 bg-muted/20 rounded-lg cursor-pointer hover:bg-muted/40 transition-colors"
                onClick={() => {
                  setInputTimestamp(item.value.toString())
                  copyToClipboard(item.value.toString(), item.label)
                }}
              >
                <p className="text-xs text-muted-foreground">{item.label}</p>
                <p className="font-mono text-sm">{item.value}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(item.value * 1000).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
